import { useEffect, useRef, useState } from "react";
import { Button, Input, Modal, NoticeBox } from "../../ui";

// Adds another draft store to the signed-in customer's OWN account (backend: POST /api/auth/stores/mine). Only the essentials are asked for;
// the full onboarding is completed afterwards from the dashboard. No subscription or payment is involved.

const TEXT = {
  ar: {
    title: "إضافة متجر جديد",
    description: "سيُنشأ متجر جديد بصيغة مسودة على حسابك الحالي. يمكنك إكمال بياناته والاشتراك لاحقًا.",
    businessName: "اسم المتجر (بالعربية)",
    businessNameEn: "اسم المتجر (بالإنجليزية)",
    contactName: "اسم المسؤول عن المتجر",
    phone: "رقم الجوال",
    email: "البريد الإلكتروني للتواصل",
    city: "المدينة",
    optional: "اختياري",
    submit: "إنشاء المتجر",
    cancel: "إلغاء",
    close: "إغلاق",
    required: "هذا الحقل مطلوب",
    invalidEmail: "أدخل بريدًا إلكترونيًا صحيحًا",
    failed: "تعذّر إنشاء المتجر. حاول مرة أخرى.",
    codes: {
      STORE_LIMIT_REACHED: "وصل حسابك إلى الحد الأقصى لعدد المتاجر.",
      NOT_ELIGIBLE: "لا يمكن لهذا الحساب إضافة متجر.",
      CREATION_IN_PROGRESS: "هناك عملية إنشاء متجر قيد التنفيذ، حاول بعد لحظات.",
      INVALID_EMAIL: "أدخل بريدًا إلكترونيًا صحيحًا",
      INVALID_INPUT: "أكمل الحقول المطلوبة.",
    },
  },
  en: {
    title: "Add New Store",
    description: "A new draft store is created on your current account. You can complete its details and subscribe later.",
    businessName: "Store name (Arabic)",
    businessNameEn: "Store name (English)",
    contactName: "Store contact name",
    phone: "Contact phone number",
    email: "Contact email",
    city: "City",
    optional: "Optional",
    submit: "Create store",
    cancel: "Cancel",
    close: "Close",
    required: "This field is required",
    invalidEmail: "Enter a valid email address",
    failed: "The store could not be created. Please try again.",
    codes: {
      STORE_LIMIT_REACHED: "Your account has reached the maximum number of stores.",
      NOT_ELIGIBLE: "This account cannot add a store.",
      CREATION_IN_PROGRESS: "A store is already being created. Try again in a moment.",
      INVALID_EMAIL: "Enter a valid email address",
      INVALID_INPUT: "Complete the required fields.",
    },
  },
};

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const newRequestId = () => {
  const bytes = new Uint8Array(16);
  const webCrypto = window.crypto;
  if (webCrypto?.getRandomValues) webCrypto.getRandomValues(bytes);
  else bytes.forEach((_, index) => { bytes[index] = Math.floor(Math.random() * 256); }); // very old browsers / test environments
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
};

export default function AddStoreModal({ lang = "en", defaults = {}, onClose, onCreate }) {
  const text = TEXT[lang === "ar" ? "ar" : "en"];
  const [values, setValues] = useState({
    businessName: "",
    businessNameEn: "",
    contactName: defaults.name || "",
    phone: defaults.phone || "",
    email: defaults.email || "",
    city: "",
  });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState("");
  // One id per form: a retry or a double click sends the same id, so the server returns the same store instead of creating another.
  const [requestId] = useState(newRequestId);
  const mounted = useRef(true);
  // Set on every mount: React StrictMode runs mount -> cleanup -> mount in development, so the flag must be raised again each time.
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  const set = (key) => (event) => {
    setValues((current) => ({ ...current, [key]: event.target.value }));
    setErrors((current) => ({ ...current, [key]: undefined }));
  };

  const validate = () => {
    const next = {};
    if (!values.businessName.trim()) next.businessName = text.required;
    if (!values.contactName.trim()) next.contactName = text.required;
    if (!values.phone.trim()) next.phone = text.required;
    if (!values.email.trim()) next.email = text.required;
    else if (!EMAIL.test(values.email.trim())) next.email = text.invalidEmail;
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const submit = async (event) => {
    event?.preventDefault();
    if (busy || !validate()) return;
    setBusy(true);
    setFailure("");
    try {
      await onCreate({
        businessName: values.businessName.trim(),
        businessNameEn: values.businessNameEn.trim(),
        contactName: values.contactName.trim(),
        phone: values.phone.trim(),
        email: values.email.trim(),
        city: values.city.trim(),
        requestId,
      });
    } catch (error) {
      if (!mounted.current) return;
      setFailure(text.codes[error?.code] || text.failed);
      setBusy(false);
    }
  };

  return (
    <Modal
      title={text.title}
      description={text.description}
      onClose={onClose}
      busy={busy}
      closeLabel={text.close}
      footer={
        <>
          <Button variant="neutral" appearance="ghost" onClick={onClose} disabled={busy}>
            {text.cancel}
          </Button>
          <Button type="submit" form="add-store-form" loading={busy} disabled={busy}>
            {text.submit}
          </Button>
        </>
      }
    >
      <form id="add-store-form" onSubmit={submit} noValidate dir={lang === "ar" ? "rtl" : "ltr"} style={{ display: "grid", gap: 12 }}>
        {failure && <NoticeBox variant="danger" size="sm">{failure}</NoticeBox>}
        <Input label={text.businessName} value={values.businessName} onChange={set("businessName")} error={errors.businessName} required maxLength={200} autoFocus />
        <Input label={text.businessNameEn} value={values.businessNameEn} onChange={set("businessNameEn")} helperText={text.optional} maxLength={200} dir="ltr" />
        <Input label={text.contactName} value={values.contactName} onChange={set("contactName")} error={errors.contactName} required maxLength={200} />
        <Input label={text.phone} value={values.phone} onChange={set("phone")} error={errors.phone} required type="tel" inputMode="tel" maxLength={40} dir="ltr" />
        <Input label={text.email} value={values.email} onChange={set("email")} error={errors.email} required type="email" maxLength={200} dir="ltr" />
        <Input label={text.city} value={values.city} onChange={set("city")} helperText={text.optional} maxLength={200} />
      </form>
    </Modal>
  );
}
